import React, { useCallback, useEffect } from "react";
import { useGet, usePut } from "../../api";
import {
  ITaskSimple,
  TaskStatus,
  checkoutTechnologyType,
} from "../../interfaces";
import {
  failureReasonBadge,
  failureReasonLabel,
} from "../../interfaces/failure-reasons";
import {
  Button,
  ButtonGroup,
  Table,
  Spinner,
  Form,
  Row,
  Col,
  Badge,
} from "react-bootstrap";

import { format } from "date-fns";
import { Link, useNavigate } from "react-router";
import { DeleteTasksModal } from "./delete-tasks";
import { useApi, settingsType, useSettings } from "../../hooks";
import { RunTypeBadge } from "./run-type-badge";

export const Tasks: React.FC = () => {
  const navigate = useNavigate();
  const { get } = useApi();
  const [page, setPage] = React.useState(1);
  const [limit, setLimit] = React.useState(50);

  // Persisted settings (kept in sync with localStorage)
  const [checkoutType, setCheckoutType] = useSettings(
    settingsType.TASKS_CHECKOUT_TYPE
  );
  const [noCoupons, setNoCoupons] = useSettings(settingsType.TASKS_NO_COUPONS);
  const [startDateFrom, setStartDateFrom] = useSettings(
    settingsType.TASKS_START_DATE_FROM
  );
  const [startDateTo, setStartDateTo] = useSettings(
    settingsType.TASKS_START_DATE_TO
  );
  const [ranSuccessfully, setRanSuccessfully] = useSettings(
    settingsType.TASKS_RAN_SUCCESSFULLY
  );
  const [showDeleteModal, setShowDeleteModal] = React.useState(false);

  // Pending filter state — what's shown in the inputs but not yet submitted
  const [pendingCountry, setPendingCountry] = React.useState<string>("");
  const [pendingDomain, setPendingDomain] = React.useState<string>("");
  const [pendingStatus, setPendingStatus] = React.useState<string>("");
  const [pendingCheckoutType, setPendingCheckoutType] = React.useState<string>(checkoutType ?? "");
  const [pendingNoCoupons, setPendingNoCoupons] = React.useState<string>(noCoupons ?? "all");
  const [pendingStartDateFrom, setPendingStartDateFrom] = React.useState<string>(startDateFrom ?? "");
  const [pendingStartDateTo, setPendingStartDateTo] = React.useState<string>(startDateTo ?? "");
  const [pendingRanSuccessfully, setPendingRanSuccessfully] = React.useState<string>(ranSuccessfully ?? "all");

  // Committed filter state — drives the actual API call
  const [committed, setCommitted] = React.useState({
    country: "",
    domain: "",
    status: "",
    checkoutType: checkoutType ?? "",
    noCoupons: noCoupons ?? "all",
    startDateFrom: startDateFrom ?? "",
    startDateTo: startDateTo ?? "",
    ranSuccessfully: ranSuccessfully ?? "all",
  });

  const handleSearch = () => {
    // Persist settings that use useSettings
    setCheckoutType(pendingCheckoutType);
    setNoCoupons(pendingNoCoupons);
    setStartDateFrom(pendingStartDateFrom);
    setStartDateTo(pendingStartDateTo);
    setRanSuccessfully(pendingRanSuccessfully);

    setCommitted({
      country: pendingCountry,
      domain: pendingDomain,
      status: pendingStatus,
      checkoutType: pendingCheckoutType,
      noCoupons: pendingNoCoupons,
      startDateFrom: pendingStartDateFrom,
      startDateTo: pendingStartDateTo,
      ranSuccessfully: pendingRanSuccessfully,
    });
    setPage(1);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") handleSearch();
  };

  let queryParams = `page=${page}&limit=${limit}`;
  if (committed.country) queryParams += `&countryCode=${committed.country}`;
  if (committed.domain) queryParams += `&domain=${committed.domain}`;
  if (committed.status) queryParams += `&status=${committed.status}`;
  if (committed.checkoutType)
    queryParams += `&checkoutType=${encodeURIComponent(committed.checkoutType)}`;
  if (committed.noCoupons && committed.noCoupons !== "all")
    queryParams += `&noCoupons=${committed.noCoupons}`;
  if (committed.startDateFrom)
    queryParams += `&startDateFrom=${encodeURIComponent(committed.startDateFrom)}`;
  if (committed.startDateTo)
    queryParams += `&startDateTo=${encodeURIComponent(committed.startDateTo)}`;
  if (committed.ranSuccessfully && committed.ranSuccessfully !== "all")
    queryParams += `&ranSuccessfully=${committed.ranSuccessfully}`;

  const tasks = useGet<ITaskSimple[]>(`tasks?${queryParams}`);
  const updateTask = usePut<boolean>();

  const onCancelTask = async (_id: string) => {
    await updateTask.execute(`/tasks/${_id}`, { status: TaskStatus.CANCELLED });
  };

  // Use a ref to store the current refetch function
  const tasksRefetchRef = React.useRef(tasks.refetch);

  // Update the ref whenever tasks.refetch changes
  useEffect(() => {
    tasksRefetchRef.current = tasks.refetch;
  }, [tasks.refetch]);

  const handleRefresh = useCallback(async () => {
    await tasksRefetchRef.current();
  }, []);

  const cancelOutstanding = async () => {
    await updateTask.execute(`/tasks/cancel/outstanding`);
    await handleRefresh();
  };

  const startTest = async (merchantId?: string, runType: 'live' | 'audit' = 'live') => {
    if (!merchantId) return;

    await get(`/test/browser?merchantId=${merchantId}&runType=${runType}`);
  };

  const handleClearFilters = () => {
    const cleared = {
      country: "",
      domain: "",
      status: "",
      checkoutType: "",
      noCoupons: "all",
      startDateFrom: "",
      startDateTo: "",
      ranSuccessfully: "all",
    };
    setPendingCountry("");
    setPendingDomain("");
    setPendingStatus("");
    setPendingCheckoutType("");
    setPendingNoCoupons("all");
    setPendingStartDateFrom("");
    setPendingStartDateTo("");
    setPendingRanSuccessfully("all");
    setCheckoutType("");
    setNoCoupons("all");
    setStartDateFrom("");
    setStartDateTo("");
    setRanSuccessfully("all");
    setCommitted(cleared);
    setPage(1);
  };

  const setQuickDateRange = (days: number) => {
    const to = new Date();
    const from = new Date();
    from.setDate(from.getDate() - days);
    const fromStr = from.toISOString().split("T")[0];
    const toStr = to.toISOString().split("T")[0];
    setPendingStartDateFrom(fromStr);
    setPendingStartDateTo(toStr);
    // Quick ranges commit immediately
    setStartDateFrom(fromStr);
    setStartDateTo(toStr);
    setCommitted((prev) => ({ ...prev, startDateFrom: fromStr, startDateTo: toStr }));
    setPage(1);
  };

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h2>Tasks</h2>
        <div className="d-flex align-items-center">
          <Button
            variant="outline-primary"
            className="me-2"
            onClick={handleRefresh}
          >
            <i className="bi bi-arrow-clockwise"></i>
          </Button>
          <Button
            variant="danger"
            className="me-2"
            onClick={() => setShowDeleteModal(true)}
          >
            Delete All
          </Button>
          <Button variant="warning" onClick={() => cancelOutstanding()}>
            Cancel Outstanding
          </Button>
        </div>
      </div>

      <Row className="mb-3">
        <Col md={3}>
          <Form.Group>
            <Form.Label>Country</Form.Label>
            <Form.Control
              type="text"
              placeholder="Enter country code"
              value={pendingCountry}
              onChange={(e) => setPendingCountry(e.target.value)}
              onKeyDown={handleKeyDown}
            />
          </Form.Group>
        </Col>
        <Col md={3}>
          <Form.Group>
            <Form.Label>Domain</Form.Label>
            <Form.Control
              type="text"
              placeholder="Enter domain"
              value={pendingDomain}
              onChange={(e) => setPendingDomain(e.target.value)}
              onKeyDown={handleKeyDown}
            />
          </Form.Group>
        </Col>
        <Col md={3}>
          <Form.Group>
            <Form.Label>Status</Form.Label>
            <Form.Select
              value={pendingStatus}
              onChange={(e) => setPendingStatus(e.target.value)}
            >
              <option value="">All Statuses</option>
              <option value="completed">Completed</option>
              <option value="in_progress">In Progress</option>
              <option value="pending">Pending</option>
              <option value="failed">Failed</option>
            </Form.Select>
          </Form.Group>
        </Col>
        <Col md={3}>
          <Form.Group>
            <Form.Label>Checkout Type</Form.Label>
            <Form.Select
              value={pendingCheckoutType}
              onChange={(e) => setPendingCheckoutType(e.target.value)}
            >
              <option value="">All Types</option>
              {Object.values(checkoutTechnologyType).map((type) => (
                <option key={type} value={type}>
                  {type}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        </Col>
      </Row>

      <Row className="mb-3">
        <Col md={3}>
          <Form.Group>
            <Form.Label>Result</Form.Label>
            <Form.Select
              value={pendingRanSuccessfully}
              onChange={(e) => setPendingRanSuccessfully(e.target.value)}
            >
              <option value="all">All Results</option>
              <option value="true">Success Only</option>
              <option value="false">Failed Only</option>
            </Form.Select>
          </Form.Group>
        </Col>
        <Col md={3}>
          <Form.Group>
            <Form.Label>No Coupons Filter</Form.Label>
            <Form.Select
              value={pendingNoCoupons}
              onChange={(e) => setPendingNoCoupons(e.target.value)}
            >
              <option value="all">All</option>
              <option value="true">No Coupons Only</option>
              <option value="false">Exclude No Coupons</option>
            </Form.Select>
          </Form.Group>
        </Col>
        <Col md={3}>
          <Form.Group>
            <Form.Label>Start Date From</Form.Label>
            <Form.Control
              type="date"
              value={pendingStartDateFrom}
              onChange={(e) => setPendingStartDateFrom(e.target.value)}
            />
          </Form.Group>
        </Col>
        <Col md={3}>
          <Form.Group>
            <Form.Label>Start Date To</Form.Label>
            <Form.Control
              type="date"
              value={pendingStartDateTo}
              onChange={(e) => setPendingStartDateTo(e.target.value)}
            />
          </Form.Group>
        </Col>
      </Row>

      <Row className="mb-3">
        <Col>
          <div className="d-flex gap-2 align-items-center">
            <Button variant="primary" size="sm" onClick={handleSearch}>
              Search
            </Button>
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={() => setQuickDateRange(1)}
            >
              Last 24 Hours
            </Button>
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={() => setQuickDateRange(3)}
            >
              Last 3 Days
            </Button>
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={() => setQuickDateRange(7)}
            >
              Last 7 Days
            </Button>
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={() => setQuickDateRange(30)}
            >
              Last 30 Days
            </Button>
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={handleClearFilters}
            >
              Clear Filters
            </Button>
          </div>
        </Col>
      </Row>

      <Table striped bordered hover>
        <thead>
          <tr>
            <th>ID</th>
            <th>Domain</th>
            <th>Country Code</th>
            <th>Checkout Type</th>
            <th>Run Type</th>
            <th>Status</th>
            <th>Not Suitable</th>
            <th>Result</th>
            <th>LLM ($)</th>
            <th>Start Time</th>
            <th>Last Activity</th>
            <th>Action</th>
          </tr>
        </thead>
        <tbody>
          {tasks.loading ? (
            <tr>
              <td colSpan={12} className="text-center py-4">
                <Spinner animation="border" role="status" className="me-2">
                  <span className="visually-hidden">Loading...</span>
                </Spinner>
                Loading tasks...
              </td>
            </tr>
          ) : tasks.error ? (
            <tr>
              <td colSpan={12} className="text-center text-danger py-4">
                Error loading tasks: {tasks.error.message}
              </td>
            </tr>
          ) : tasks?.data?.length === 0 ? (
            <tr>
              <td colSpan={12} className="text-center py-4">
                No tasks found
              </td>
            </tr>
          ) : (
            tasks?.data?.map((task: ITaskSimple) => (
              <tr key={task._id}>
                <td title={task._id}>{task._id.substring(0, 3)}...</td>
                <td>{task.domain}</td>
                <td>{task.countryCode}</td>
                <td>
                  {task.checkoutType ? (
                    <Badge bg="info">{task.checkoutType}</Badge>
                  ) : (
                    <span className="text-muted">N/A</span>
                  )}
                </td>
                <td>
                  <RunTypeBadge runType={task.runType} />
                </td>
                <td>{task.status}</td>
                <td>{task.notSuitable ? "Yes" : "No"}</td>
                <td>
                  {
                    <>
                      {task.notSuitable && (
                        <Badge bg="warning" className="me-1">
                          Not Suitable
                        </Badge>
                      )}
                      {task.ranSuccessfully === false &&
                        task.errorMessage !== "No coupons found to test" && (
                          <Badge
                            bg="danger"
                            className="me-1"
                            title={task.errorMessage || "Failed"}
                          >
                            Failed
                          </Badge>
                        )}
                      {task.ranSuccessfully === false &&
                        task.errorType &&
                        task.errorMessage !== "No coupons found to test" && (
                          <Badge
                            bg={failureReasonBadge(task.errorType).bg}
                            text={failureReasonBadge(task.errorType).text as any}
                            className="me-1"
                            title={task.errorMessage || undefined}
                          >
                            {failureReasonLabel(task.errorType)}
                          </Badge>
                        )}
                      {task.ranSuccessfully === true && (
                        <Badge bg="success" className="me-1">
                          Success
                        </Badge>
                      )}
                      {task.errorMessage === "No coupons found to test" && (
                        <Badge
                          bg="secondary"
                          className="me-1"
                          title="No coupons found to test"
                        >
                          No Coupons
                        </Badge>
                      )}
                      {task.requiredHumanIntervention && (
                        <Badge bg="warning" className="me-1">
                          Human Needed
                        </Badge>
                      )}
                      {task.status === TaskStatus.IN_PROGRESS && (
                        <Badge bg="info" className="me-1">
                          In Progress
                        </Badge>
                      )}
                      {task.status === TaskStatus.CANCELLED && (
                        <Badge bg="secondary" className="me-1">
                          Cancelled
                        </Badge>
                      )}
                    </>
                  }
                </td>
                <td>${(task.llmTotalCost ?? 0).toFixed(2)}</td>
                <td>
                  {format(new Date(task.startTime), "yyyy-MM-dd HH:mm:ss")}
                </td>
                <td>
                  {task.lastActivity
                    ? format(new Date(task.lastActivity), "yyyy-MM-dd HH:mm:ss")
                    : "N/A"}
                </td>
                <td>
                  <ButtonGroup>
                    <Button
                      as={Link as any}
                      variant="outline-primary"
                      size="sm"
                      to={`/tasks/${task._id}`}
                    >
                      <i className="bi bi-eye"></i>
                    </Button>
                    <Button variant="outline-warning" size="sm">
                      <i className="bi bi-hand-index-thumb"></i>
                    </Button>
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      onClick={() => onCancelTask(task._id)}
                    >
                      <i className="bi bi-stop-circle"></i>
                    </Button>
                    <Button
                      variant="outline-success"
                      size="sm"
                      onClick={() =>
                        // Session runs are relaunched via /test/session-run;
                        // the rerun button restarts live/audit only.
                        startTest(
                          task.merchantId,
                          task.runType === 'audit' ? 'audit' : 'live'
                        )
                      }
                    >
                      <i className="bi bi-arrow-clockwise"></i>
                    </Button>
                    <Button variant="outline-danger" size="sm">
                      <i className="bi bi-x-circle"></i>
                    </Button>
                  </ButtonGroup>
                </td>
              </tr>
            ))
          )}
        </tbody>
        <tfoot>
          <tr>
            <td colSpan={12} className="text-center">
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setPage(page - 1)}
                disabled={page <= 1}
              >
                <i className="bi bi-chevron-left"></i>
              </Button>
              <span className="mx-2">Page {page}</span>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setPage(page + 1)}
                disabled={!tasks.data || tasks.data.length < limit}
              >
                <i className="bi bi-chevron-right"></i>
              </Button>
            </td>
          </tr>
        </tfoot>
      </Table>
      <DeleteTasksModal
        show={showDeleteModal}
        onClose={async () => {
          setShowDeleteModal(false);
          await tasks.refetch();
        }}
      />
    </div>
  );
};
